/** Clinical and financial insight cards for Connections to Behavioral Care. */
export interface ConnectionsBhCareInsightCard {
  heading: string;
  body: string;
  citationLabel: string;
  citationUrl: string;
}

export interface ConnectionsBhCareInsights {
  clinical: ConnectionsBhCareInsightCard;
  financial: ConnectionsBhCareInsightCard;
}

const CLINICAL_CITATION_URL =
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC3485479/';
const FINANCIAL_CITATION_URL =
  'https://pmc.ncbi.nlm.nih.gov/articles/PMC11164411/';
const DEFAULT_PER_MEMBER_SAVINGS = 1146;

const formatCurrency = (amount: number): string =>
  amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

/**
 * Builds clinical and financial insight copy for the BH connections section.
 * @param membersConnected - Members connected to behavioral health care.
 * @param costReductionPredBh - Optional warehouse predicted total savings.
 * @returns Insight cards with dynamic member and savings values.
 */
export const buildConnectionsBhCareInsights = (
  membersConnected: number,
  costReductionPredBh = 0,
): ConnectionsBhCareInsights => {
  const perMemberSavings =
    membersConnected > 0 && costReductionPredBh > 0
      ? Math.round(costReductionPredBh / membersConnected)
      : DEFAULT_PER_MEMBER_SAVINGS;

  return {
    clinical: {
      heading:
        'Connection to behavioral health treatment reduces symptoms and improves chronic disease management.',
      body: '56% of members receiving treatment for depression typically achieve a 50% reduction in PHQ-9 scores or move below the clinical threshold for moderate depression within 12 months.',
      citationLabel: 'Citation',
      citationUrl: CLINICAL_CITATION_URL,
    },
    financial: {
      heading: 'Behavioral health treatment lowers the total cost of care.',
      body: `For the ${membersConnected.toLocaleString()} members now connected to behavioral health care, treatment is associated with an average ${formatCurrency(perMemberSavings)} reduction in annual healthcare spending per member with co-occurring chronic conditions.`,
      citationLabel: 'Citation',
      citationUrl: FINANCIAL_CITATION_URL,
    },
  };
};
